/**
 * Identity — port of BackendService.prepareAnonymousAccount plus the web-only
 * e-mail link. Anonymous first; e-mail is offered at purchase time and in settings.
 */
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

export interface AuthSnapshot {
  userID: string;
  isAnonymous: boolean;
  email?: string;
  accessToken: string;
}

export async function prepareAnonymousAccount(): Promise<AuthSnapshot> {
  const sb = supabase();
  const { data } = await sb.auth.getSession();
  let session: Session | null = data.session;
  if (!session) {
    const { data: anon, error } = await sb.auth.signInAnonymously();
    if (error || !anon.session) throw error ?? new Error('Anonymous sign-in failed');
    session = anon.session;
  }
  return toSnapshot(session);
}

export function toSnapshot(session: Session): AuthSnapshot {
  return {
    userID: session.user.id,
    isAnonymous: session.user.is_anonymous ?? !session.user.email,
    email: session.user.email ?? undefined,
    accessToken: session.access_token,
  };
}

/** Converts the anonymous account into a permanent one (Supabase sends a confirmation link). */
/** The player's UI language, stored in user_metadata.locale so Supabase e-mail templates can localize ({{ .Data.locale }}). */
export async function linkEmail(email: string, redirectTo: string, locale?: string): Promise<void> {
  const { error } = await supabase().auth.updateUser({ email, ...(locale ? { data: { locale } } : {}) }, { emailRedirectTo: redirectTo });
  if (error) throw error;
}

/** Magic link sign-in on a new device. */
export async function sendMagicLink(email: string, redirectTo: string, locale?: string): Promise<void> {
  const { error } = await supabase().auth.signInWithOtp({ email, options: { emailRedirectTo: redirectTo, shouldCreateUser: false, ...(locale ? { data: { locale } } : {}) } });
  if (error) throw error;
}

export async function currentAccessToken(): Promise<string | undefined> {
  const { data } = await supabase().auth.getSession();
  return data.session?.access_token;
}

export async function signOut(): Promise<void> {
  await supabase().auth.signOut();
}
